import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { personal, stats } from "../data/portfolio";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const gridRef = useRef(null);
  const h1Ref = useRef(null);

  useEffect(() => {
    // GSAP: subtle grid parallax on mousemove
    const onMove = (e) => {
      const xPct = (e.clientX / window.innerWidth - 0.5) * 20;
      const yPct = (e.clientY / window.innerHeight - 0.5) * 20;
      gsap.to(gridRef.current, {
        x: xPct,
        y: yPct,
        duration: 1.2,
        ease: "power2.out",
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useEffect(() => {
    // GSAP: letter-by-letter animation on h1
    if (!h1Ref.current) return;
    const chars = h1Ref.current.querySelectorAll(".char");
    gsap.fromTo(
      chars,
      { y: "110%", opacity: 0 },
      {
        y: "0%",
        opacity: 1,
        duration: 0.7,
        stagger: 0.04,
        ease: "power3.out",
        delay: 0.5,
      }
    );
  }, []);

  const nameChars = "YUVAN".split("").map((c, i) => (
    <span key={i} className="char" style={{ display: "inline-block" }}>
      {c}
    </span>
  ));

  return (
    <section
      id="hero"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "9rem 3rem 4rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated grid bg */}
      <div
        ref={gridRef}
        style={{
          position: "absolute",
          inset: "-5%",
          backgroundImage: `
            linear-gradient(rgba(57,255,20,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(57,255,20,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* Glow blob */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          right: "-10%",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(57,255,20,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <motion.div variants={container} initial="hidden" animate="show">
        {/* Badge */}
        <motion.div variants={fadeUp}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.6rem",
              fontFamily: "var(--font-mono)",
              fontSize: "0.72rem",
              color: "var(--accent)",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              border: "1px solid rgba(57,255,20,0.3)",
              padding: "0.35rem 0.9rem",
              marginBottom: "1.5rem",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--accent)",
                animation: "pulse 2s infinite",
              }}
            />
            Full-Stack Developer & GenAI Explorer
          </div>
        </motion.div>

        {/* H1 with GSAP chars */}
        <div
          ref={h1Ref}
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(5.5rem, 15vw, 14rem)",
            lineHeight: 0.86,
            letterSpacing: "0.01em",
            marginBottom: "1.2rem",
            overflow: "hidden",
          }}
        >
          <div style={{ overflow: "hidden" }}>
            {nameChars}
            <span
              className="char"
              style={{ display: "inline-block", color: "var(--accent)" }}
            >
              _
            </span>
          </div>
          <div style={{ overflow: "hidden" }}>
            {"R.".split("").map((c, i) => (
              <span key={i} className="char" style={{ display: "inline-block" }}>
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* Subtext */}
        <motion.p
          variants={fadeUp}
          style={{
            fontSize: "1.05rem",
            color: "var(--muted2)",
            maxWidth: "520px",
            lineHeight: 1.75,
            marginBottom: "2.5rem",
          }}
        >
          {personal.tagline}
          <br />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.78rem",
              color: "var(--muted)",
            }}
          >
            
          </span>
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={fadeUp}
          style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}
        >
          <a href="#projects" style={{ textDecoration: "none" }}>
            <motion.button
              whileHover={{ backgroundColor: "var(--accent)", color: "#000" }}
              whileTap={{ scale: 0.97 }}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.72rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "0.9rem 2rem",
                border: "1px solid var(--accent)",
                background: "transparent",
                color: "var(--accent)",
                cursor: "none",
                transition: "background 0.2s, color 0.2s",
              }}
            >
              View Projects →
            </motion.button>
          </a>
          <a href={personal.github} target="_blank" rel="noreferrer">
            <motion.button
              whileHover={{ borderColor: "var(--muted2)", color: "var(--text)" }}
              whileTap={{ scale: 0.97 }}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.72rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "0.9rem 2rem",
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                cursor: "none",
                transition: "all 0.2s",
              }}
            >
              GitHub ↗
            </motion.button>
          </a>
          <a href={personal.linkedin} target="_blank" rel="noreferrer">
            <motion.button
              whileHover={{ borderColor: "var(--muted2)", color: "var(--text)" }}
              whileTap={{ scale: 0.97 }}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.72rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "0.9rem 2rem",
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--muted)",
                cursor: "none",
                transition: "all 0.2s",
              }}
            >
              LinkedIn ↗
            </motion.button>
          </a>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={fadeUp}
          style={{
            display: "flex",
            gap: "3rem",
            flexWrap: "wrap",
            marginTop: "4rem",
            paddingTop: "2rem",
            borderTop: "1px solid var(--border)",
          }}
        >
          {stats.map((s) => (
            <div key={s.label}>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "3rem",
                  color: "var(--accent2)",
                  lineHeight: 1,
                }}
              >
                {s.num}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.68rem",
                  color: "var(--muted)",
                  letterSpacing: "0.1em",
                  marginTop: "0.3rem",
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        style={{
          position: "absolute",
          bottom: "2.5rem",
          right: "3rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.65rem",
            color: "var(--muted)",
            letterSpacing: "0.15em",
            writingMode: "vertical-rl",
          }}
        >
          SCROLL
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          style={{
            width: 1,
            height: 40,
            background: "linear-gradient(var(--accent), transparent)",
          }}
        />
      </motion.div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </section>
  );
}
